import React, { useState } from 'react'
import LabeledValue from '../../../../components/common/LabeledValue'
import Modal from '../../../../components/common/Modal/Modal'
import { TimeOffRequestDetails, isVacationBid } from '../../../../store/slices/Requestor/timeOffDetailsSlice'
import { normalizeStatus } from './constants'
import { getVacationBidAwareTableProps } from './vacationBidOverlapDisplay'
import RightPanel from './RightPanel'
import CurrentRequestSection from './CurrentRequestSection'
import HistorySection from './HistorySection'
import WithdrawnView from './WithdrawnView'
import { useSelector } from 'react-redux'
import useWithdrawRequest from '../../../../hooks/useWithdrawRequest'

interface ResubmittedTimeOffViewProps {
  data: TimeOffRequestDetails
  tableRows: any[]
  highlightedDates: string[]
  handlePrint?: () => void
}

const ResubmittedTimeOffView: React.FC<ResubmittedTimeOffViewProps> = ({
  data,
  tableRows,
  highlightedDates,
  handlePrint,
}) => {
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false)
  const [withdrawn, setWithdrawn] = useState(false)
  const empNuid = useSelector((state: any) => state.profile?.profile?.empNuid) || sessionStorage.getItem('userId')
  const { withdraw, loading: withdrawLoading, error: withdrawError } = useWithdrawRequest()
  const ns = normalizeStatus(data.status)
  const isOnHold = ns === 'resubmitted_onhold'
  const isReleased = ns === 'resubmitted_released'
  const heading = isOnHold
    ? 'Resubmitted Time Off Request On Hold'
    : 'Resubmitted Time Off Request'
  if (withdrawn) {
    return (
      <WithdrawnView
        data={{ ...data, status: 'Withdrawn' }}
        tableRows={tableRows}
        highlightedDates={highlightedDates}
        handlePrint={handlePrint}
        showSuccessBanner
      />
    )
  }

const getResubmittedBy = () => {
  // data.updatedBy is the TOR record's last-updated-by field (not necessarily
  // the resubmitter); the RESUBMITTED status entry's own updatedBy — the same
  // actor already embedded in data.resubmittedBy — is the correct comparison.
  // Not gated on manualReqRcvdDate — a proxy can resubmit a request the
  // employee originally submitted themselves, and vice versa.
  const updatedBy = data.statuses?.[0]?.updatedBy?.trim().toLowerCase()
  const nuid = data.nuid?.trim().toLowerCase()

  if (updatedBy && nuid && updatedBy !== nuid) {
    return `Requestor Proxy ${data.resubmittedBy}`
  }

  return data.resubmittedBy
}

  return (
    <div>
      <h1 className="h1">{heading}</h1>

      {/* Left Details + Right Employee Panel */}
      <div className="flex flex-col mobile:gap-4 tablet:gap-6 desktop:gap-8 desktop:flex-row !mt-[60px]">
        {/* Left Panel */}
        <div className="flex-1 ">
          {!isOnHold && !isReleased && (
            <h2 className="p3 !mb-[40px]">
              This time off request was re-submitted and is pending review by your approver.
            </h2>
          )}
          {(isOnHold || isReleased) && data.holdPlacedOn && (
            <div className="mb-4">
              <LabeledValue
                label={isReleased ? 'Hold Released On' : 'Hold Placed On'}
                value={data.holdPlacedOn}
              />
            </div>
          )}

          {(isOnHold || isReleased) && data.holdPlacedBy && (
            <div className="mb-4">
              <LabeledValue
                label={isReleased ? 'Released By' : 'Hold Placed By'}
                value={data.holdPlacedBy}
              />
            </div>
          )}
          {isReleased && (data.requestRank != null || isVacationBid(data)) && (
            <div className="mb-4">
              <LabeledValue label="Request Rank" value={data.requestRank == null ? 'NA' : String(data.requestRank)} />
            </div>
          )}
          {!isReleased && data.resubmittedOn && (
            <div className="mb-4">
              <LabeledValue label="Resubmitted On" value={data.resubmittedOn} />
            </div>
          )}
          {!isReleased && data.resubmittedBy && (
            <div className="mb-4">
              <LabeledValue label="Resubmitted By" value={getResubmittedBy()} />
            </div>
          )}
          {isReleased ? (
            <div className="mb-4">
              <LabeledValue
                label="Approver Comments"
                value={data.approverComments || '-'}
              />
            </div>
          ) : (
            <div className="mb-4">
              <LabeledValue
                label="Employee Comments"
                value={data.employeeComments || '-'}
              />
            </div>
          )}

          <button type="button"
            className="button -primary -inverted mt-8!"
            onClick={() => setIsWithdrawOpen(true)}
          >
            Withdraw Request
          </button>
        </div>

        {/* Right Panel */}
        <RightPanel data={data} variant="full" handlePrint={handlePrint} />
      </div>

      {/* Divider */}
      <div className="mt-6!">
        <hr className="-variation-1" />
      </div>

      {/* History */}
      {data.history && data.history.length > 0 && (
        <HistorySection history={data.history} />
      )}

      {/* Current Request */}
      <CurrentRequestSection
        {...getVacationBidAwareTableProps(tableRows, isVacationBid(data))}
        highlightedDates={highlightedDates}
      />

      {/* Withdraw Modal */}
      <Modal
        isOpen={isWithdrawOpen}
        title="Withdraw Request"
        onClose={() => setIsWithdrawOpen(false)}
        submitBtn="Withdraw Request"
        backBtn="Cancel"
        loading={withdrawLoading}
        onPrimary={async () => {
          const nuid = empNuid || sessionStorage.getItem('userId') || sessionStorage.getItem('nuid') || ''
          const requestId = data.requestId || ''
          if (!requestId || !nuid) {
            console.error('Missing required data for withdrawal:', { requestId, nuid })
            return false
          }
          const success = await withdraw(requestId, nuid)
          if (success) {
            setIsWithdrawOpen(false)
            setWithdrawn(true)
          }
          return success
        }}
        onBack={() => setIsWithdrawOpen(false)}
      >
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
          }}
        >
          <img
            className="w-10 h-10 shrink-0"
            src="/assets/images/alertSolid.svg"
            alt="warning"
          />

          <div>
            <p className="p2 -semibold">
              You are requesting to withdraw this time off request. It will be
              removed from the approvers queue.
            </p>
            {withdrawError && (
              <p className="p3 -medium text-red-600 mt-2">{withdrawError}</p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ResubmittedTimeOffView
